import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Loader2, PenLine, CheckCircle2, Eraser, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter122k67 · Componente pubblico "Firma i documenti".
 * Renderizzato dentro SchedaProfessionistaPublic dopo che la scheda è stata inviata.
 * A+B: canvas grafico + OTP email.
 */
export default function SchedaFirmaPublic({ token, api, docs, onSigned }) {
  // docs = [{tipo, label, preview_url}, ...] (link anteprima ai PDF NON firmati)
  const [accept, setAccept] = useState({ convenzione: false, privacy: false, consenso: false });
  const [otpSent, setOtpSent] = useState(false);
  const [emailMasked, setEmailMasked] = useState("");
  const [otp, setOtp] = useState("");
  const [busyOtp, setBusyOtp] = useState(false);
  const [busySign, setBusySign] = useState(false);
  const [error, setError] = useState("");
  const sigRef = useRef(null);
  const [empty, setEmpty] = useState(true);

  const clearSig = () => { sigRef.current?.clear(); setEmpty(true); };

  useEffect(() => {
    const onEnd = () => setEmpty(sigRef.current?.isEmpty() ?? true);
    // Poll a semplice fallback: SignatureCanvas non espone onEnd nativamente su tutti i device
    const t = setInterval(onEnd, 500);
    return () => clearInterval(t);
  }, []);

  const allAccepted = accept.convenzione && accept.privacy && accept.consenso;
  const canRequestOtp = allAccepted && !empty && !busyOtp;
  const canSign = otpSent && otp.length === 6 && !empty && allAccepted && !busySign;

  const richiediOtp = async () => {
    setError("");
    setBusyOtp(true);
    try {
      const { data } = await api.post(`/credius-scheda/${token}/firma/richiedi-otp`, {});
      setEmailMasked(data.email_masked || "");
      setOtpSent(true);
      toast.success(`Codice inviato a ${data.email_masked}. Valido ${data.expires_in_minutes} minuti.`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Invio codice non riuscito";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusyOtp(false);
    }
  };

  const firma = async () => {
    setError("");
    setBusySign(true);
    try {
      // Iter122k69 · getTrimmedCanvas può fallire su alcuni browser mobile → fallback al canvas full.
      let signature_png = "";
      try {
        signature_png = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
      } catch (canvasErr) {
        try { signature_png = sigRef.current.toDataURL("image/png"); } catch { signature_png = ""; }
      }
      if (!signature_png || signature_png.length < 200) {
        throw new Error("Impossibile leggere la firma dal canvas. Prova a rifirmare.");
      }
      const { data } = await api.post(`/credius-scheda/${token}/firma/conferma`, {
        otp, signature_png,
        accept_convenzione: accept.convenzione,
        accept_privacy: accept.privacy,
        accept_consenso: accept.consenso,
      });
      toast.success("Documenti firmati con successo!");
      onSigned?.(data);
    } catch (e) {
      // Iter122k69 · Estrai messaggio errore leggibile per l'utente
      let msg = "Firma non riuscita";
      if (e.message && !e.response) msg = e.message;  // errore locale (canvas ecc.)
      else if (e.response?.data?.detail) {
        const detail = e.response.data.detail;
        if (typeof detail === "string") msg = detail;
        else if (Array.isArray(detail)) msg = detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
        else msg = JSON.stringify(detail);
      } else if (e.response?.status) {
        msg = `Errore server (HTTP ${e.response.status})`;
      } else if (e.message) msg = e.message;
      // eslint-disable-next-line no-console
      console.error("[FIRMA]", e);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusySign(false);
    }
  };

  const toggle = (k) => setAccept((a) => ({ ...a, [k]: !a[k] }));

  return (
    <div data-testid="firma-public" className="space-y-4">
      <div className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-indigo-900 mb-2">
          <PenLine className="w-5 h-5" />
          <h2 className="font-bold">Firma elettronica dei documenti</h2>
        </div>
        <p className="text-sm text-slate-700">
          Per formalizzare la convenzione firma <b>digitalmente</b> i 3 documenti qui sotto (Firma Elettronica Avanzata
          ai sensi dell'art. 20 CAD e Reg. UE eIDAS 910/2014).
        </p>
      </div>

      {/* Anteprime + checkbox */}
      <div className="space-y-2">
        {docs.map((d) => (
          <div key={d.tipo} data-testid={`firma-doc-${d.tipo}`}
            className="flex items-start gap-3 border-2 border-slate-200 rounded-lg bg-white p-3">
            <input type="checkbox" data-testid={`firma-accept-${d.tipo}`}
              className="mt-1 w-5 h-5 accent-indigo-800"
              checked={!!accept[d.tipo]} onChange={() => toggle(d.tipo)} />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm text-slate-800">{d.label}</div>
              <a href={d.preview_url} target="_blank" rel="noopener noreferrer"
                data-testid={`firma-anteprima-${d.tipo}`}
                className="text-xs text-indigo-700 hover:underline break-all">
                📄 Apri anteprima PDF
              </a>
              <div className="text-[11px] text-slate-500 mt-1">
                Dichiaro di aver letto e di accettare integralmente il contenuto del documento.
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Signature pad */}
      <div className="bg-white border-2 border-slate-200 rounded-xl p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-slate-700">Firma qui sotto (con dito o mouse)</span>
          <button type="button" onClick={clearSig} data-testid="firma-cancella-btn"
            className="text-xs flex items-center gap-1 text-slate-500 hover:text-red-600">
            <Eraser className="w-3.5 h-3.5" /> Cancella
          </button>
        </div>
        <div className="border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-slate-50">
          <SignatureCanvas
            ref={sigRef}
            penColor="#111827"
            canvasProps={{
              className: "w-full h-40 bg-white",
              "data-testid": "firma-canvas",
            }}
          />
        </div>
        {empty && <div className="text-[11px] text-slate-400 mt-1">Traccia la firma per attivare il codice OTP.</div>}
      </div>

      {/* Richiedi OTP */}
      {!otpSent && (
        <button type="button" data-testid="firma-richiedi-otp"
          disabled={!canRequestOtp} onClick={richiediOtp}
          className="w-full h-12 bg-indigo-900 hover:bg-indigo-950 disabled:opacity-40 text-white font-bold rounded-lg flex items-center justify-center gap-2">
          {busyOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Invia codice OTP via email
        </button>
      )}

      {/* OTP input + Firma */}
      {otpSent && (
        <div className="space-y-3 bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
          <div className="text-sm text-emerald-800">
            Codice OTP inviato a <b>{emailMasked}</b>. Inseriscilo qui sotto (valido 30 minuti).
          </div>
          <input
            data-testid="firma-otp-input"
            inputMode="numeric" maxLength={6} pattern="[0-9]{6}"
            value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="w-full h-14 text-center text-2xl font-bold tracking-widest border-2 border-emerald-300 rounded-lg bg-white"
            placeholder="_ _ _ _ _ _"
          />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={richiediOtp} disabled={busyOtp}
              className="text-xs text-emerald-800 hover:underline disabled:opacity-40">
              Non ricevuto? Invia di nuovo
            </button>
          </div>
          <button type="button" data-testid="firma-conferma-btn"
            disabled={!canSign} onClick={firma}
            className="w-full h-12 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white font-bold rounded-lg flex items-center justify-center gap-2">
            {busySign ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
            Firma tutti i documenti
          </button>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border-2 border-red-200 rounded-lg p-3">{error}</div>
      )}

      <p className="text-[11px] text-slate-500 text-center">
        Firma Elettronica Avanzata · La firma grafica + verifica OTP sul dispositivo garantiscono
        integrità, autenticità e non ripudio del documento sottoscritto (art. 20 CAD, Reg. UE eIDAS 910/2014).
      </p>
    </div>
  );
}
